import React, { useEffect, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiEyeOutline } from "@mdi/js";
import { Chip, SearchField, Table } from "@heroui/react";
import { clientDetailStore } from "@/stores/ClientDetailStore";
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_COLOR,
} from "../../ContractorDetailPage/constants";
import { formatMoneyRub } from "@shared-utils/helpers";
import {
  SortPanel,
  SortableColumnHeader,
} from "../../ContractorDetailPage/components/shared";
import { DateRangeFilter } from "@/shared/ui/DateRangeFilter";
import { CombinedFilter } from "@/shared/ui/CombinedFilter";
import { LoadMoreSentinel } from "@/shared/ui/LoadMoreSentinel";
import { ChipWithTooltip } from "@/shared/ui/ChipWithTooltip";
import { useSortPanel } from "@/hooks/useSortPanel";

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  unpaid: "Не оплачено",
  pending: "Ожидание",
  paid: "Оплачено",
  refunded: "Возврат",
  partially_refunded: "Частичный возврат",
};

const PAYMENT_STATUS_COLOR: Record<
  string,
  "warning" | "success" | "danger" | "default"
> = {
  unpaid: "warning",
  pending: "warning",
  paid: "success",
  refunded: "danger",
  partially_refunded: "danger",
};

const SOURCE_LABEL: Record<string, string> = {
  client_app: "Приложение",
  admin_panel: "Админка",
};

const BK_SORT_COLUMNS = [
  { id: "start_time", label: "Дата" },
  { id: "total_cost", label: "Сумма" },
];

const BookingsTab: React.FC = observer(() => {
  const store = clientDetailStore;
  const sort = useSortPanel("start_time");
  const handleLoadMore = useCallback(() => {
    store.bk.loadMore();
  }, [store]);

  const handleOpenPayment = (paymentUuid: string, description: string) => {
    store.pd.open(paymentUuid, description, "payment", "booking");
  };

  useEffect(() => {
    store.bk.load();
  }, []);

  // Серверная сортировка — при изменении перезагружаем
  useEffect(() => {
    const prefix = sort.sortDescriptor.direction === "descending" ? "-" : "";
    const newSort = `${prefix}${sort.sortDescriptor.column}`;
    if (newSort !== store.bk.sort) {
      store.bk.setSort(newSort);
      store.bk.load(1);
    }
  }, [sort.sortDescriptor]);

  const searchTimer = useRef<number>(0);

  const handleSearch = (val: string) => {
    store.bk.setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => store.bk.load(1), 600);
  };

  const handleStatusFilter = (val: string[]) => {
    store.bk.setStatusFilter(val);
    store.bk.load(1);
  };

  const handlePaymentFilter = (val: string[]) => {
    store.bk.setPaymentFilter(val);
    store.bk.load(1);
  };

  const handleDateRange = (range: { start: string; end: string } | null) => {
    let endDate = range?.end ?? "";
    if (endDate) {
      const d = new Date(endDate);
      d.setDate(d.getDate() + 1);
      endDate = d.toISOString().slice(0, 10);
    }
    store.bk.setDateRange(range?.start ?? "", endDate);
    store.bk.load(1);
  };

  return (
    <div className="cdp-tab-content" data-testid="cdp-bookings-tab">
      <div className="cdp-toolbar">
        <div className="cdp-toolbar__left">
          <SearchField
            value={store.bk.search}
            onChange={handleSearch}
            aria-label="Поиск бронирований"
            className="cdp-search"
            data-testid="cdp-bookings-search"
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="СТО, услуга, авто..." />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>

          <CombinedFilter
            groups={[
              {
                id: "status",
                label: "Статус",
                options: [
                  { id: "pending", label: "Создан" },
                  { id: "confirmed", label: "Подтверждено" },
                  { id: "in_progress", label: "В работе" },
                  { id: "completed", label: "Завершено" },
                  { id: "cancelled", label: "Отменено" },
                ],
                value: store.bk.statusFilter,
                onChange: handleStatusFilter,
              },
              {
                id: "payment",
                label: "Оплата",
                options: [
                  { id: "unpaid", label: "Не оплачено" },
                  { id: "pending", label: "Ожидание" },
                  { id: "paid", label: "Оплачено" },
                  { id: "refunded", label: "Возврат" },
                ],
                value: store.bk.paymentFilter,
                onChange: handlePaymentFilter,
              },
            ]}
            extra={
              <>
                <div className="cdp-combined-filter__group-label">Период</div>
                <div style={{ padding: "4px 12px 8px" }}>
                  <DateRangeFilter
                    label="Период бронирований"
                    onChange={handleDateRange}
                  />
                </div>
              </>
            }
          />
        </div>
        <div className="cdp-toolbar__right">
          <SortPanel
            columns={BK_SORT_COLUMNS}
            sortDescriptor={sort.sortDescriptor}
            setSortDescriptor={sort.setSortDescriptor}
            isSortOpen={sort.isSortOpen}
            setIsSortOpen={sort.setIsSortOpen}
            sortAnchorRef={sort.sortAnchorRef}
            sortPanelRef={sort.sortPanelRef}
            testIdPrefix="cdp-bookings"
          />
        </div>
      </div>

      <div className="cdp-table-wrap" data-testid="cdp-bookings-table">
        <Table variant="secondary">
          <Table.ScrollContainer className="cdp-scroll-container">
            <Table.Content
              aria-label="Бронирования"
              className="cdp-table cdp-table--bookings cdp-table--cdp-bk"
              sortDescriptor={sort.sortDescriptor}
              onSortChange={(d) =>
                sort.setSortDescriptor({
                  column: String(d.column),
                  direction: d.direction as "ascending" | "descending",
                })
              }
            >
              <Table.Header className="cdp-table__head cdp-table__head--sticky">
                <Table.Column id="start_time" isRowHeader allowsSorting>
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Дата и время
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="sc">СТО</Table.Column>
                <Table.Column id="service">Услуга</Table.Column>
                <Table.Column id="car">Авто</Table.Column>
                <Table.Column
                  id="total_cost"
                  className="cdp-table__th_right"
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Сумма
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="payment">Оплата</Table.Column>
                <Table.Column id="status">Статус</Table.Column>
                <Table.Column id="source">Источник</Table.Column>
                <Table.Column
                  id="actions"
                  className="cdp-table__th--actions"
                  style={{ width: 80 }}
                >
                  <span className="sr-only">Действия</span>
                </Table.Column>
              </Table.Header>
              <Table.Body>
                {store.bk.items.length === 0 ? (
                  <Table.Row className="cdp-table__row">
                    <Table.Cell className="cdp-table__td" colSpan={9}>
                      <div className="cdp-table-empty">
                        {store.bk.isLoading ? "Загрузка..." : "Нет данных"}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  store.bk.items.map((b) => {
                    const start = new Date(b.startTime);
                    const end = new Date(b.endTime);
                    const paymentLabel =
                      PAYMENT_STATUS_LABEL[b.paymentStatus] ?? b.paymentStatus;
                    const bookingStatusLabel =
                      BOOKING_STATUS_LABEL[b.status] ?? b.status;
                    const dateFormatted = start.toLocaleDateString("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                    });
                    const timeRange = `${start.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}–${end.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`;
                    const carLabel = b.car
                      ? `${b.car.make} ${b.car.model}`.trim()
                      : "—";

                    return (
                      <Table.Row
                        key={b.uuid}
                        id={b.uuid}
                        data-uuid={b.uuid}
                        className="cdp-table__row"
                      >
                        <Table.Cell className="cdp-table__td cdp-table__td--date">
                          <span className="cdp-datetime-cell">
                            <span className="cdp-datetime-cell__date">
                              {dateFormatted}
                            </span>
                            <span className="cdp-datetime-cell__time">
                              {timeRange}
                            </span>
                          </span>
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td">
                          {b.serviceCenterName ?? "—"}
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td">
                          {b.service?.name ?? "—"}
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td">
                          {carLabel}
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td cdp-table__td_right">
                          {formatMoneyRub(b.totalCost)}
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td">
                          <ChipWithTooltip
                            label={paymentLabel}
                            color={
                              PAYMENT_STATUS_COLOR[b.paymentStatus] ?? "default"
                            }
                          />
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td">
                          <ChipWithTooltip
                            label={bookingStatusLabel}
                            color={BOOKING_STATUS_COLOR[b.status] ?? "default"}
                          />
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td">
                          <Chip
                            variant="soft"
                            size="sm"
                            color={
                              b.source === "client_app" ? "accent" : "default"
                            }
                          >
                            {SOURCE_LABEL[b.source] ?? b.source}
                          </Chip>
                        </Table.Cell>
                        <Table.Cell className="cdp-table__td cdp-table__td--actions">
                          <button
                            type="button"
                            className="cdp-eye-btn"
                            aria-label="Просмотр платежа"
                            disabled={!b.paymentUuid}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (b.paymentUuid) {
                                handleOpenPayment(
                                  b.paymentUuid,
                                  `Платёж за бронирование${b.service ? " · " + b.service.name : ""}`,
                                );
                              }
                            }}
                          >
                            <Icon path={mdiEyeOutline} size={0.8} />
                          </button>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
        <LoadMoreSentinel
          onLoadMore={handleLoadMore}
          isLoading={store.bk.isLoading}
          hasMore={store.bk.meta.hasNextPage}
        />
      </div>
    </div>
  );
});

BookingsTab.displayName = "BookingsTab";
export default BookingsTab;
